import { useEffect, useState, useRef } from 'react';
import { getAPI } from '~/lib/api';
import { API_PATHS } from '@habity/constants';
import { NestedArea, NestedMunicipality } from '@habity/types';
import { extractPolygonsFromGeometries } from '~/lib/map';

export const useMapPolygons = ({
  areaIds,
  municipalityIds,
}: {
  areaIds?: number[] | undefined;
  municipalityIds?: number[] | undefined;
}) => {
  const [municipalityPolygons, setMunicipalityPolygons] = useState<
    GeoJSON.Polygon[]
  >([]);
  const [areaPolygons, setAreaPolygons] = useState<GeoJSON.Polygon[]>([]);

  const prevMunicipalityIds = useRef<string>('');
  const prevAreaIds = useRef<string>('');

  async function fetchMunicipalitiesData(ids: number[] | undefined) {
    if (ids?.length && ids.length > 0) {
      const idsString = ids.sort().join(',');

      if (prevMunicipalityIds.current !== idsString) {
        prevMunicipalityIds.current = idsString;

        const queryParamIds = ids.join(',');
        const { data: municipalitiesData }: { data: NestedMunicipality[] } =
          await getAPI(
            `${API_PATHS.PROVIDERS.SUPABASE.MUNICIPALITIES.INDEX}?ids=${queryParamIds}`
          );
        const municipalityGeometries = municipalitiesData.map(
          (m) => m.location as GeoJSON.Geometry
        );
        const _municipalityPolygons = extractPolygonsFromGeometries(
          municipalityGeometries
        );
        setMunicipalityPolygons(_municipalityPolygons);
      }
    }
  }

  async function fetchAreasData(ids: number[] | undefined) {
    if (ids?.length && ids.length > 0) {
      const idsString = ids.sort().join(',');

      if (prevAreaIds.current !== idsString) {
        prevAreaIds.current = idsString;

        const queryParamIds = ids.join(',');
        const { data: areasData }: { data: NestedArea[] } = await getAPI(
          `${API_PATHS.PROVIDERS.SUPABASE.AREAS.INDEX}?ids=${queryParamIds}`
        );
        const areaGeometries = areasData.map(
          (a) => a.location as GeoJSON.Geometry
        );
        const _areaPolygons = extractPolygonsFromGeometries(areaGeometries);
        setAreaPolygons(_areaPolygons);
      }
    }
  }

  useEffect(() => {
    if (!municipalityIds || municipalityIds.length === 0) {
      setMunicipalityPolygons([]);
      prevMunicipalityIds.current = '';
    } else {
      fetchMunicipalitiesData(municipalityIds);
    }
  }, [municipalityIds]);

  useEffect(() => {
    if (!areaIds || areaIds.length === 0) {
      setAreaPolygons([]);
      prevAreaIds.current = '';
    } else {
      fetchAreasData(areaIds);
    }
  }, [areaIds]);

  // Return empty array instead of undefined for consistent map reset behavior
  return {
    polygons: municipalityPolygons.concat(areaPolygons),
  };
};
